import { ArrowRightIcon, EyeIcon, MoreIcon, OpenInNewIcon } from '../icons'
import Button from '../buttons/button'
import TypographyParagraph from '../typography/TypographyParagraph'
import TypographyLabel from '../typography/TypographyLabel'
import clsx from 'clsx'
import Comment from './comment'
import ProofOfExhibition from './proofOfExhibition'
import Tweet from './tweet'
import Cast from './cast'
import Instagram from './instagram'
import OffchainLend from './offchainLend'
import OffchainSale from './offchainSale'
import Revoke from './revoke'
import Rollup from './rollup'
import Countersign from './countersign'
import MediaCitation from './mediaCitation'
import Media from './media'
import PopupMenu from './menu'
import Collection from './collection'
import { Link, useLocation } from '@remix-run/react'
import InternalLink from '../common/internalLink'
import { type Theme } from '#app/utils/providers/themeProvider'
import {
	formatMetadataType,
	getCollectionPreviewURL,
	getCollectionProfileURL,
	getMetadataProfileURL,
	getNFTProfileURL,
	getUserProfileURL,
	mapIcon,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import { getTimelineAttestationDateString } from '#app/utils/timeUtils'
import Bulk from './bulk'
import { DynamicContext } from '@dynamic-labs/sdk-react-core'
import { Tag } from '../badgesAndTags'

const MetadataEntry = ({
	isPreview = false,
	metadata,
	additionalClasses,
	tweetsUrl,
	displayEmbedContent = true,
	displayIcon = false,
	nftName,
	collectionName,
	userProfileUrl,
	theme,
	onHighlightAttestation,
	highlightedAttestations = [],
}: {
	isPreview?: boolean
	metadata: any
	additionalClasses?: string
	tweetsUrl?: string
	displayEmbedContent?: boolean
	displayIcon?: boolean
	nftName?: string
	collectionName?: string
	userProfileUrl?: string
	theme: Theme | undefined
	onHighlightAttestation?: (attestationIPFSCID: string, userId: string) => Promise<void>
	highlightedAttestations?: any[]
}) => {
	const metadataProfileUrl = getMetadataProfileURL(metadata)
	let collectionProfileUrl = getCollectionProfileURL(metadata)
	let nftProfileUrl = getNFTProfileURL(metadata)
	const location = useLocation()

	const onHighlight = async (attestationIPFSCID: string) => {
		if (onHighlightAttestation) {
			await onHighlightAttestation(attestationIPFSCID, metadata.userId);
		}
	};

	if (location.pathname.includes('nftProfile')) {
		nftProfileUrl = ''
	} else if (location.pathname.includes('collection')) {
		collectionProfileUrl = ''
	}

	const mapText = (metadata: any) => {
		if (metadata.template.slug == 'collection' && metadata.artistName) {
			return `Onchain Collection`
		}
		if (metadata.template.slug == 'countersign' && metadata.originalReference) {
			if (
				metadata.originalReference.countersigningAddress &&
				metadata.address &&
				metadata.originalReference.countersigningAddress.toLowerCase() ===
					metadata.address.toLowerCase()
			) {
				return (
					<TypographyLabel
						size="medium"
						additionalClasses="mt-0 text-textSecondaryLight dark:text-textSecondaryDark flex h-auto sm:h-[20px] items-end sm:items-center"
					>
						countersigned{' '}
						{metadata.originalReference && metadata.originalReference.template
							? metadata.originalReference.template.name
							: ''}
					</TypographyLabel>
				)
			}
		} else if (metadata.template.slug === 'collection') {
			return (
				<TypographyLabel
					size="medium"
					additionalClasses="mt-0 text-textSecondaryLight dark:text-textSecondaryDark flex h-auto sm:h-[20px] items-end sm:items-center"
				>
					signed{' '}
					<InternalLink
						to={getCollectionProfileURL(metadata)}
						additionalClasses="ml-0.5"
					>
						{formatMetadataType(metadata.template.slug, metadata.template.name)}
					</InternalLink>
				</TypographyLabel>
			)
		} else {
			return (
				<TypographyLabel
					size="medium"
					additionalClasses="mt-0 text-textSecondaryLight dark:text-textSecondaryDark flex h-auto sm:h-[20px] items-end sm:items-center"
				>
					signed{' '}
					{formatMetadataType(metadata.template.slug, metadata.template.name)}
				</TypographyLabel>
			)
		}
	}

	return (
		<div className={'flex w-full gap-x-1'}>
			{displayIcon && (
				<div className="ml-4 mt-2 flex h-full w-4 items-start justify-center text-textPrimaryLight dark:text-textPrimaryDark">
					{metadataProfileUrl ? (
						<Link
							className="mr-2 inline-flex h-6 w-6 items-center justify-center"
							to={`/${metadataProfileUrl}`}
						>
							{mapIcon(
								metadata.template.slug,
								'large',
								metadata.properties.fileType || '',
							)}
						</Link>
					) : (
						<div className="mr-2 inline-flex h-6 w-6 items-center justify-center">
							{mapIcon(
								metadata.template.slug,
								'large',
								metadata.properties.fileType || '',
							)}
						</div>
					)}
				</div>
			)}
			<div
				className={clsx(
					'flex min-w-0 flex-1 flex-col pt-1 sm:pt-2',
					isPreview ? 'mr-4 pb-4' : '',
					additionalClasses,
				)}
			>
				<div className="flex flex-row">
					<div className="relative flex w-full flex-col">
						<div className="flex w-full flex-row justify-between pb-4">
							<div className="flex flex-col sm:flex-row sm:gap-x-1 md:flex-col">
								<div className="flex flex-row items-center gap-x-1">
									{userProfileUrl ? (
										<InternalLink to={userProfileUrl}>
											<Button
												size="medium"
												variant="tertiary"
												additionalClasses="p-0 h-6 sm:mt-0 mt-[2px] justify-start cursor-pointer"
											>
												<TypographyLabel size="medium" isBold={true}>
													@
												</TypographyLabel>
												<TypographyLabel size="medium" isBold={true}>
													{metadata.artistName
														? `${metadata.artistName}'s`
														: metadata.username
															? metadata.username
															: truncateStringInMiddle(metadata.address)}
												</TypographyLabel>
											</Button>
										</InternalLink>
									) : (
										<Button
											size="medium"
											variant="tertiary"
											additionalClasses="p-0 h-6 sm:mt-0 mt-[2px] justify-start hover:no-underline cursor-default"
										>
											<TypographyLabel
												size="medium"
												isBold={true}
												additionalClasses=""
											>
												@
											</TypographyLabel>
											<TypographyLabel
												size="medium"
												isBold={true}
												additionalClasses="cursor-default hover:no-underline"
											>
												{metadata.artistName
													? metadata.artistName
													: metadata.username
														? metadata.username
														: truncateStringInMiddle(metadata.address)}
											</TypographyLabel>
										</Button>
									)}
									<TypographyLabel
										size="medium"
										additionalClasses="hidden md:block mt-0 text-textSecondaryLight dark:text-textSecondaryDark flex h-auto items-end sm:items-center"
									>
										{mapText(metadata)}
									</TypographyLabel>
								</div>
								<TypographyLabel
									size="small"
									additionalClasses="flex items-center whitespace-nowrap text-textSecondaryLight dark:text-textSecondaryDark ml-0 md:pt-1"
								>
									{getTimelineAttestationDateString(
										metadata.comparisonDate || metadata.createdAt,
										true,
									)}
									<InternalLink
										to={`${metadataProfileUrl}`}
										additionalClasses="inline-flex items-center hover:opacity-80"
									>
										<OpenInNewIcon className="ml-2 h-4 w-4 shrink-0" />
									</InternalLink>
								</TypographyLabel>
							</div>
							<div>
								{isPreview && metadata.template.slug !== 'collection' ? (
									<Tag
										size="medium"
										variant="fill"
										icon={<EyeIcon />}
										showIcon={true}
									>
										{' '}
										Preview
									</Tag>
								) : null}
								{isPreview && metadata.template.slug === 'collection' ? (
									<InternalLink
										to={getCollectionPreviewURL(metadata)}
										target="_blank"
									>
										<Button
											variant="primary"
											size="small"
											leftIcon={<EyeIcon />}
										>
											Preview Collection
										</Button>
									</InternalLink>
								) : null}
							</div>
						</div>
						{((nftProfileUrl &&
							nftName &&
							(metadata.references.entry.chain || metadata.references.chain)) ||
							collectionName) && (
							<div className="mb-2 flex items-center text-textSecondaryLight dark:text-textSecondaryDark">
								<TypographyParagraph
									size="xSmall"
									additionalClasses="pt-0.5 text-textSecondaryLight dark:text-textSecondaryDark"
								>
									Attested to
								</TypographyParagraph>
								<InternalLink
									to={nftProfileUrl || getCollectionProfileURL(metadata)}
									prefetch="none"
									additionalClasses="flex text-[#7676FC]"
								>
									<ArrowRightIcon size="small" className="ml-2 h-4 w-4"></ArrowRightIcon>
									<TypographyLabel
										size="xSmall"
										additionalClasses="pt-0.5 ml-2"
									>
										{nftName || collectionName}
									</TypographyLabel>
								</InternalLink>
							</div>
						)}
						{metadata.targetWalletAddress && (
							<div className="mb-2 flex items-center text-textSecondaryLight dark:text-textSecondaryDark">
								<TypographyParagraph
									size="xSmall"
									additionalClasses="pt-0.5 text-textSecondaryLight dark:text-textSecondaryDark"
								>
									Attested to
								</TypographyParagraph>
								<InternalLink
									to={getUserProfileURL(metadata.userId)}
									prefetch="none"
									additionalClasses="flex text-[#7676FC]"
								>
									<ArrowRightIcon size="small" className="ml-2 h-4 w-4"></ArrowRightIcon>
									<TypographyLabel
										size="xSmall"
										additionalClasses="pt-0.5 ml-2"
									>
										{metadata.username
											? `${metadata.username}`
											: truncateStringInMiddle(metadata.targetWalletAddress)}
									</TypographyLabel>
								</InternalLink>
							</div>
						)}
					</div>
					{metadata.references && metadata.references.attestationIPFSCID && (
						<div className="flex h-full w-auto flex-grow items-start justify-end">
							{DynamicContext._currentValue ? (
								<PopupMenu
									menuButton={<MoreIcon />}
									metadataEntry={metadata}
									metadataType={formatMetadataType(
										metadata.template.slug,
										metadata.template.name,
									)}
									nftProfileUrl={nftProfileUrl}
									metadataProfileUrl={metadataProfileUrl}
									collectionProfileUrl={collectionProfileUrl}
									onHighlight={() => onHighlight(metadata.references.attestationIPFSCID)}
									highlightedAttestations={highlightedAttestations}
								/>
							) : (
								<div className="relative inline-block text-left">
									<div className="text-textPrimaryLight dark:text-textPrimaryDark">
										<MoreIcon />
									</div>
								</div>
							)}
						</div>
					)}
				</div>
				<div className={clsx(isPreview ? 'mr-8' : '')}>
					{metadata.template.slug == 'comment' && (
						<Comment metadata={metadata} />
					)}
					{metadata.template.slug == 'proofOfExhibition' && (
						<ProofOfExhibition metadata={metadata} />
					)}
					{metadata.template.slug == 'tweet' && (
						<Tweet
							metadata={metadata}
							displayEmbedContent={displayEmbedContent}
							tweetsUrl={tweetsUrl}
						/>
					)}
					{metadata.template.slug == 'media' && (
						<Media metadata={metadata} isPreview={isPreview} />
					)}
					{metadata.template.slug == 'cast' && (
						<Cast
							metadata={metadata}
							displayEmbedContent={displayEmbedContent}
						/>
					)}
					{metadata.template.slug == 'instagram' && (
						<Instagram
							metadata={metadata}
							displayEmbedContent={displayEmbedContent}
							theme={theme}
						/>
					)}
					{metadata.template.slug == 'mediaCitation' && (
						<MediaCitation metadata={metadata} theme={theme} />
					)}
					{metadata.template.slug == 'offchainLend' && (
						<OffchainLend metadata={metadata} />
					)}
					{metadata.template.slug == 'offchainSale' && (
						<OffchainSale metadata={metadata} />
					)}
					{metadata.template.slug == 'collection' && (
						<Collection metadata={metadata} theme={theme} />
					)}
					{metadata.template.slug == 'revoke' && <Revoke metadata={metadata} />}
					{metadata.template.slug == 'countersign' && (
						<Countersign metadata={metadata} />
					)}
					{metadata.template.slug == 'rollup' && <Rollup metadata={metadata} />}
					{metadata.template.slug == 'bulk' && <Bulk metadata={metadata} />}
				</div>
			</div>
		</div>
	)
}

export default MetadataEntry
